// src/admin/screens/admins/AdminsListScreen.tsx
// Super-admin management tool. Shows every admin row, their role, whether
// they've enrolled TOTP, whether they're in read-only mode, and their last
// login. Super-admins get an "Invite admin" button that opens a modal;
// "Manage" links to the detail screen where role / TOTP / deactivation live.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import DataTable, { type Column } from '@admin/components/DataTable';
import Button from '@admin/components/Button';
import StatusChip from '@admin/components/StatusChip';
import SectionHead from '@admin/components/SectionHead';
import Avatar from '@admin/components/Avatar';
import { useAdmins, type AdminRow } from '@admin/hooks/useAdmins';
import { useRole } from '@admin/auth/useRole';
import { formatCatDateTime } from '@admin/utils/dates';
import InviteAdminModal from './modals/InviteAdminModal';
import styles from './AdminsListScreen.module.css';

export default function AdminsListScreen() {
  const { can } = useRole();
  const { data, isLoading, error, refetch } = useAdmins();
  const [invite, setInvite] = useState(false);
  const rowCount = data?.length ?? 0;

  const columns = useMemo<Column<AdminRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Admin',
        render: (r) => (
          <div className={styles.adminCell}>
            <Avatar name={r.display_name} size={32} />
            <div>
              <div className={styles.name}>{r.display_name}</div>
              <div className={styles.email}>{r.email}</div>
            </div>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        render: (r) => <code className={styles.code}>{r.role}</code>,
        width: '160px',
      },
      {
        key: 'readonly',
        header: 'Mode',
        width: '120px',
        render: (r) =>
          r.is_readonly
            ? <StatusChip label="Read-only" tone="warn" />
            : <span className={styles.muted}>Read-write</span>,
      },
      {
        key: 'status',
        header: 'Status',
        width: '120px',
        render: (r) =>
          r.is_active
            ? <StatusChip label="Active" tone="positive" />
            : <StatusChip label="Deactivated" tone="danger" />,
      },
      {
        key: 'totp',
        header: 'TOTP',
        width: '110px',
        render: (r) =>
          r.totp_enrolled_at
            ? <StatusChip label="Enrolled" tone="positive" />
            : <StatusChip label="Pending" tone="warn" />,
      },
      {
        key: 'login',
        header: 'Last login',
        width: '180px',
        render: (r) =>
          r.last_login_at
            ? <span className="mono" style={{ fontSize: 12.5, color: 'var(--c-text-muted)' }}>{formatCatDateTime(r.last_login_at)}</span>
            : <span className={styles.muted}>never</span>,
      },
      {
        key: 'actions',
        header: '',
        width: '90px',
        align: 'right',
        render: (r) => (
          <Link to={`/admins/${r.id}`} className={styles.linkBtn}>Manage</Link>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Access control"
        title="Admins"
        description={`${rowCount} staff member${rowCount === 1 ? '' : 's'} · TOTP required for all`}
        action={
          can('admins.invite') && (
            <Button onClick={() => setInvite(true)} leftIcon={<UserPlus size={14} />}>
              Invite admin
            </Button>
          )
        }
      />
      <DataTable
        rows={data}
        columns={columns}
        isLoading={isLoading}
        error={(error as Error | null)?.message ?? null}
        rowKey={(r) => r.id}
        emptyLabel="No admins yet. Bootstrap the first super_admin via scripts/bootstrap-admin.ts."
      />
      {invite && (
        <InviteAdminModal
          onClose={(ok) => {
            setInvite(false);
            if (ok) void refetch();
          }}
        />
      )}
    </div>
  );
}
